import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker in quick add modal or product page
 * 2. Swatches variant picker in product cards
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event
   */
  updatePrice = (event) => {
    const eventData = event.detail?.data;
    if (!eventData?.html) return;

    if (eventData.newProduct) {
      this.dataset.productId = String(eventData.newProduct.id);
    } else if (
      eventData.productId &&
      this.dataset.productId &&
      String(eventData.productId) !== String(this.dataset.productId)
    ) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    const blockId = this.dataset.blockId;
    const productId = this.dataset.productId;

    let newProductPrice = null;

    if (blockId) {
      newProductPrice = eventData.html.querySelector(
        `product-price[data-block-id="${CSS.escape(blockId)}"]`
      );
    }

    if (!newProductPrice && productId) {
      newProductPrice = eventData.html.querySelector(
        `product-price[data-product-id="${CSS.escape(String(productId))}"]`
      );
    }

    if (!newProductPrice) {
      newProductPrice = eventData.html.querySelector('product-price');
    }

    if (!newProductPrice) {
      console.warn('Updated product price could not be found in the variant response.');
      return;
    }

    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');

    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice.cloneNode(true));
    }

    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      const currentPrice = this.querySelector('[ref="priceContainer"]');
      currentPrice?.insertAdjacentElement(
        'afterend',
        /** @type {Element} */ (newNote.cloneNode(true))
      );
    } else {
      volumePricingNote.replaceWith(newNote.cloneNode(true));
    }

    if (blockId) {
      const inputSelector = `#product-form-installment-${CSS.escape(blockId)} input[name="id"]`;
      const installmentsInput = /** @type {HTMLInputElement | null} */ (
        this.querySelector(inputSelector)
      );

      if (installmentsInput) {
        installmentsInput.value = event.detail.resource?.id ?? '';
        installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
